import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "primereact/button";
import { useContext, useEffect, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Toast } from "primereact/toast";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

const enum INTENT_TYPE {
  FREE_TRIAL = "freeTrial",
  PAYMENT = "payment"
}

const CheckoutForm = ({ clientSecret, intentType, returnUrl, submitLabel = "Checkout", onPaymentSuccess, telemetryAction }) => {
  const stripe = useStripe();
  const toast = useRef<Toast>(null);
  const elements = useElements();
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const mobile = useResponsive().isMobile;

  useEffect(() => {
    const setBillingDetails = async () => {
      try {
        await backendAPIStore.init();
      }
      catch (error: any) {
        toast.current?.show({ severity: "error", summary: "Error", detail: error.message || "Failed to fetch user profile data", life: 3000 });
      }
    }
    setBillingDetails();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      setMessage(submitError.message!);
      return;
    }

    setIsLoading(true);

    const finalReturnUrl = returnUrl || `${window.location.origin}/billing`;

    let result;
    if (intentType === INTENT_TYPE.FREE_TRIAL) {
      result = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: finalReturnUrl,
        },
      });
    } else if (intentType === INTENT_TYPE.PAYMENT) {
      result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: finalReturnUrl,
        },
      });
    }

    if (result.error) {
      setMessage(result.error.message!);
    } else if (result?.setupIntent?.status === "succeeded" && onPaymentSuccess) {
      await onPaymentSuccess(result.setupIntent.id, "setup");
    } else if (result?.paymentIntent?.status === "succeeded" && onPaymentSuccess) {
      await onPaymentSuccess(result.paymentIntent.id, "payment");
    }

    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="d-flex flex-column align-items-center">
      <Toast ref={toast} />
      <PaymentElement />
      <div className="col d-flex justify-content-center align-items-center">
        <Button
          style={{ width: mobile ? "100%" : "30%" }}
          label={submitLabel}
          type="submit"
          disabled={isLoading || !stripe || !elements}
          loading={isLoading}
          severity="warning"
          rounded
          className="m-4"
          data-telemetry-action={telemetryAction}
        />
      </div>
      {/* Show any error or success messages */}
      {message && <div id="payment-message">{message}</div>}
    </form>
  );
};

export default CheckoutForm;
